import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { AiLeadSummaryResponse, LeadsService } from '@services/api/leads.service';
import { ToastrService } from 'ngx-toastr';
import { EMPTY, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, finalize, map, switchMap, takeUntil } from 'rxjs/operators';

type ReportNotesSummarySource = 'ai' | 'manual';

@Component({
  selector: 'app-lead-report-notes-summary',
  templateUrl: './lead-report-notes-summary.component.html',
  styleUrls: ['./lead-report-notes-summary.component.scss'],
})
export class LeadReportNotesSummaryComponent implements OnInit, OnChanges, OnDestroy {
  @Input() editLeadForm: FormGroup;
  @Input() isLeadEdit = false;
  @Input() leadId: string;
  // Backward-compatible no-op inputs to avoid template binding breakage in v1/v2 variants.
  @Input() inspectionTime: string | null = null;
  @Input() commissionStats: any[] = [];
  @Input() commissionTypeAdded: any[] = [];
  /** When false, summary updates stay local until the parent lead detail has finished loading. */
  @Input() canPersistSummary = false;
  @Output() summaryPersistRequested = new EventEmitter<void>();
  @Output() summaryChanged = new EventEmitter<string>();

  isGeneratingSummary = false;
  error_message = '';
  /** null until the user edits the summary or AI generates one in this session. */
  summarySource: ReportNotesSummarySource | null = null;
  private readonly destroy$ = new Subject<void>();
  private readonly summaryFetch$ = new Subject<{ persist: boolean }>();
  private subscriptionsBound = false;
  private isApplyingAiSummary = false;

  constructor(
    private readonly leadsService: LeadsService,
    private readonly toastr: ToastrService
  ) {}

  get summaryValue(): string {
    return this.editLeadForm?.controls?.['report_notes_summary']?.value || '';
  }

  get heading(): string {
    return 'Report Notes Summary';
  }

  get isCollapsed(): boolean {
    return this.summaryValue === '';
  }

  get isAutoUpdatePaused(): boolean {
    return this.summarySource === 'manual';
  }

  requestAiSummary(): void {
    this.setSummarySource('ai');
    this.fetchAiSummary(true);
  }

  onSummaryManualEdit(): void {
    if (this.isApplyingAiSummary) {
      return;
    }
    this.setSummarySource('manual');
  }

  ngOnInit(): void {
    this.bindSummaryFetchPipeline();
    this.bindFormSubscriptions();
    this.restoreSummarySource();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['editLeadForm']) {
      this.bindFormSubscriptions();
    }
    if (changes['leadId']) {
      this.restoreSummarySource();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** @param _reportNotes Ignored; the API derives summary from lead data on the server. */
  autoUpdateSummary(_reportNotes?: string, persist?: boolean): void {
    if (this.isAutoUpdatePaused) {
      return;
    }
    this.fetchAiSummary(persist ?? this.canPersistSummary);
  }

  maybeBackfillReportNotesSummaryAfterLoad(): void {
    if (this.summaryValue.trim()) {
      return;
    }
    this.fetchAiSummary(true);
  }

  private fetchAiSummary(persist: boolean): void {
    if (!this.leadId) {
      return;
    }
    this.summaryFetch$.next({ persist });
  }

  private bindSummaryFetchPipeline(): void {
    this.summaryFetch$
      .pipe(
        debounceTime(400),
        switchMap(({ persist }) => {
          if (!this.leadId) {
            return EMPTY;
          }

          this.isGeneratingSummary = true;
          return this.leadsService.getAiLeadSummary(this.leadId).pipe(
            map(response => ({ persist, summary: this.extractSummaryText(response) })),
            catchError(() => {
              this.toastr.error('Unable to generate AI summary. Please try again.');
              return EMPTY;
            }),
            finalize(() => {
              this.isGeneratingSummary = false;
            })
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(({ persist, summary }) => {
        this.applySummary(summary, persist);
      });
  }

  private extractSummaryText(response: AiLeadSummaryResponse): string {
    if (response?.status === false) {
      this.toastr.error(response?.message || 'Unable to generate AI summary.');
      return '';
    }

    const data = response?.data;
    if (data?.ai_error) {
      this.error_message = 'This summary is not generated by AI, because AI is not configured for this office.';
      // this.toastr.error(typeof data.ai_error === 'string' ? data.ai_error : 'Unable to generate AI summary.');
      // return '';
    }

    return (data?.summary ?? '').trim();
  }

  private applySummary(summaryText: string, persist: boolean): void {
    if (!summaryText) {
      return;
    }

    if (this.summaryValue === summaryText) {
      this.setSummarySource('ai');
      return;
    }

    this.isApplyingAiSummary = true;
    this.editLeadForm?.patchValue(
      {
        report_notes_summary: summaryText,
      },
      { emitEvent: false }
    );
    this.isApplyingAiSummary = false;
    this.setSummarySource('ai');
    this.summaryChanged.emit(summaryText);
    if (persist) {
      this.summaryPersistRequested.emit();
    }
  }

  private bindFormSubscriptions(): void {
    if (!this.editLeadForm || this.subscriptionsBound) {
      return;
    }

    const reportNotesControl = this.editLeadForm.get('report_notes');
    if (reportNotesControl) {
      reportNotesControl.valueChanges.pipe(debounceTime(400), distinctUntilChanged(), takeUntil(this.destroy$)).subscribe(() => {
        this.autoUpdateSummary(undefined, false);
      });
    }

    ['inspection_dateTime', 'inspection_booked', 'inspection_complete', 'inspection_cancelled'].forEach(controlName => {
      const control = this.editLeadForm.get(controlName);
      if (!control) {
        return;
      }
      control.valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$)).subscribe(() => {
        this.autoUpdateSummary(undefined, this.canPersistSummary);
      });
    });

    this.subscriptionsBound = true;
  }

  private setSummarySource(source: ReportNotesSummarySource): void {
    this.summarySource = source;
    this.persistSummarySource(source);
  }

  private getSummarySourceStorageKey(): string | null {
    return this.leadId ? `lead-report-summary-source:${this.leadId}` : null;
  }

  private persistSummarySource(source: ReportNotesSummarySource): void {
    const key = this.getSummarySourceStorageKey();
    if (key) {
      sessionStorage.setItem(key, source);
    }
  }

  private restoreSummarySource(): void {
    const key = this.getSummarySourceStorageKey();
    if (!key) {
      this.summarySource = null;
      return;
    }

    const stored = sessionStorage.getItem(key);
    this.summarySource = stored === 'ai' || stored === 'manual' ? stored : null;
  }
}
